import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton } from '../premium';
import { saveCoachProgram } from '../../lib/coachPlan';
import type { WorkoutPlanResult, OptimizedSplitResult } from '../../coachTypes';

export function SaveCoachPlanCard({
  plan,
  split,
  onSaved,
  onDismiss,
}: {
  plan: WorkoutPlanResult;
  split?: OptimizedSplitResult | null;
  onSaved?: () => void;
  onDismiss?: () => void;
}) {
  const nav = useNavigate();
  const [title, setTitle] = useState(plan.split?.trim() || 'Coach program');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await saveCoachProgram(plan, split ?? null, title);
      setMsg('Saved to My Programs and set as active.');
      onSaved?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save program.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-4">
      <p className="text-sm font-semibold text-emerald-100">Save this program</p>
      <p className="mt-1 text-xs text-emerald-200/80">
        {plan.days.length} day{plan.days.length === 1 ? '' : 's'} · {plan.summary.slice(0, 120)}
        {plan.summary.length > 120 ? '…' : ''}
      </p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Program name"
        className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <PrimaryButton className="!min-h-[44px]" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Add to My Programs'}
        </PrimaryButton>
        <button
          type="button"
          onClick={() => nav('/workouts/program')}
          className="rounded-xl border border-white/10 px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] hover:bg-white/[0.05]"
        >
          View programs
        </button>
        {onDismiss ? (
          <button type="button" onClick={onDismiss} className="text-xs text-[var(--text-muted)] underline-offset-2 hover:underline">
            Dismiss
          </button>
        ) : null}
      </div>
      {msg ? <p className="mt-2 text-xs text-emerald-300">{msg}</p> : null}
      {err ? <p className="mt-2 text-xs text-red-300">{err}</p> : null}
    </div>
  );
}
